import {
  BlotterTable,
  BlotterTableExtrasMenu,
  BlotterTableFilters,
  Button,
  ButtonVariants,
  CollapseAllButton,
  Divider,
  EXTRAS_MENU_TOOLTIP_PROPS,
  ExpandAllButton,
  ExportMenuItems,
  FormControlSizes,
  IconName,
  MixpanelEvent,
  Toggle,
  useBlotterTableExtrasMenu,
  useCallbackRef,
  useMixpanel,
} from '@talos/kyoko';

import { ShowByButtons } from '@src/containers/Portfolio/HistoricalBalance/components/ShowByButtons';
import { CONTROL_TOOLTIPS } from '../tooltips';
import { BalancesBlotterSummaryLine } from './SummaryLine/BalancesBlotterSummaryLine';
import type { BalancesV2TabProps } from './types';
import { useBalancesV2BlotterFoundation } from './useBalancesV2BlotterFoundation';

export { balancesBlotterAggSpecs } from './useBalancesV2BlotterFoundation';

interface BalancesV2BlotterParams {
  blotterID: string;
  tab: BalancesV2TabProps;
  onCloneTab: () => void;
  onUpdateTab: (updatedTab: BalancesV2TabProps) => void;
  /** When mounting should the filter accordian be expanded */
  initialIsOpen?: boolean;
}

export const BalancesV2Blotter = ({
  blotterID,
  tab,
  onUpdateTab,
  onCloneTab,
  initialIsOpen,
}: BalancesV2BlotterParams) => {
  const mixpanel = useMixpanel();
  const {
    blotterTable,
    filterBuilderAccordion,
    groupAccounts,
    handleGroupAccountsToggle,
    handleShowByChanged,
    selectedRows,
    showBy,
    showTotals,
    showZeroBalances,
    updateShowTotals,
    updateShowZeroBalances,
  } = useBalancesV2BlotterFoundation({
    blotterID,
    tab,
    onUpdateTab,
    initialIsOpen,
    enableContextMenus: true,
    enableRowSelection: true,
  });

  const extrasMenuPopover = useBlotterTableExtrasMenu();

  const { expandAllGroups, collapseAllGroups } = blotterTable;

  const handleCloneTab = useCallbackRef(() => {
    mixpanel.track(MixpanelEvent.CloneTab);
    onCloneTab();
    extrasMenuPopover.close();
  });

  return (
    <>
      <BlotterTableFilters
        {...filterBuilderAccordion}
        {...blotterTable.blotterTableFiltersProps}
        accordionBodyPrefix={
          <ShowByButtons showBy={showBy} onClick={handleShowByChanged} size={FormControlSizes.Default} />
        }
        prefix={
          <>
            <ExpandAllButton onClick={expandAllGroups} data-testid="expand-all-button" />
            <CollapseAllButton onClick={collapseAllGroups} data-testid="collapse-all-button" />
          </>
        }
        suffix={
          <BlotterTableExtrasMenu {...extrasMenuPopover}>
            <Toggle
              size={FormControlSizes.Small}
              checked={showTotals}
              onChange={updateShowTotals}
              label="Show Totals"
            />
            <Toggle
              tooltip={CONTROL_TOOLTIPS.showZeroBalances}
              tooltipProps={EXTRAS_MENU_TOOLTIP_PROPS}
              size={FormControlSizes.Small}
              checked={showZeroBalances}
              onChange={updateShowZeroBalances}
              label="Show Zero Balances"
            />
            <Toggle
              disabled={showBy === 'asset'}
              size={FormControlSizes.Small}
              checked={groupAccounts}
              onChange={handleGroupAccountsToggle}
              label="Group Accounts"
            />
            <Divider orientation="horizontal" />
            <ExportMenuItems blotterTable={blotterTable} handleClose={extrasMenuPopover.close} />
            <Button
              startIcon={IconName.Duplicate}
              variant={ButtonVariants.Default}
              size={FormControlSizes.Small}
              onClick={handleCloneTab}
            >
              Clone Tab
            </Button>
          </BlotterTableExtrasMenu>
        }
      />
      <BlotterTable {...blotterTable} />
      <BalancesBlotterSummaryLine rows={selectedRows} />
    </>
  );
};
